import { Injectable } from '@angular/core';
import {
  compact,
  filter,
  find,
  get,
  groupBy,
  has,
  isArray,
  isNil,
  isNumber,
  isObject,
  isString,
  join,
  map,
  reduce,
  reject,
  replace,
  sum,
  toString,
  zip,
} from 'lodash';
import { Observable } from 'rxjs';

import { Address } from '@lockton-apps/shared/interfaces';

import { MultiColTableComponent } from '../components/multi-col-table.component';
import { SimpleTableComponent } from '../components/simple-table.component';
import { ValuesTableComponent } from '../components/values-table.component';
import { FormlyFieldConfigWithDisplayViewOptions } from '../interfaces';
import {
  PBArrayConfig,
  PBConfigItem,
  PBConfigItemType,
  PBFieldConfig,
  PBGroupConfig,
} from '../interfaces/presentation-builder-config.model';
import {
  PBArrayValueMapper2,
  PBFieldValueData,
  PBTextValueData,
  PBValueData,
  PBValueDataType,
  PBValueMapper2,
} from '../interfaces/presentation-builder-value-data.model';
import { PresentationBuilderValueAccessor } from './presentation-builder-value-accessor.service';
import { valueAccessors } from '.';

@Injectable({
  providedIn: 'root',
})
export class DynamicFormsPresentationValueAccessor extends PresentationBuilderValueAccessor<FormlyFieldConfigWithDisplayViewOptions> {
  formlyValueAccessors: Record<
    string,
    PBValueMapper2<FormlyFieldConfigWithDisplayViewOptions>
  > = {
    string: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    input: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    textarea: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    currency: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    number: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    datepicker: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    'select-box': (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    select: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    address: (field, value) => {
      const data = [this.simpleTableRowFrom(field, value)];
      return { component: SimpleTableComponent, inputs: { data } };
    },
    'simple-table': (field, value) => {
      const tableRows = get(field, 'props.tableRows', []);
      const align = get(
        field,
        ['displayViewOptions', 'props', 'align'],
        'start'
      );

      const entries = map(tableRows, (row) => ({
        label: row.label,
        value: map(row?.fields, (field) => {
          const fieldValue = get(value, field.key);
          return this.simpleTableRowFrom(field, fieldValue);
        }),
      }));

      return {
        component: MultiColTableComponent,
        inputs: { data: { entries }, alignValues: align },
      };
    },
    'calc-table': (field, value) => {
      const tableRows = get(field, 'props.tableRows', []);
      const align = get(
        field,
        ['displayViewOptions', 'props', 'align'],
        'start'
      );

      const entries = map(tableRows, (row) => ({
        label: row.label,
        value: map(row?.fields, (field) => {
          const fieldValue = get(value, field.key);
          return this.simpleTableRowFrom(field, fieldValue);
        }),
      }));

      const values = map(entries, (entry) => map(entry.value, 'rawValue'));
      const joined = zip(...values);
      const totals = map(joined, (subTotals) => {
        const total = sum(filter(subTotals, isNumber));
        return total || '';
      });

      const displaySummary = get(field, 'props.summary', false);
      const summaryLabel = get(field, 'props.summary.label', 'Total');
      const summaryPrefix = get(field, 'props.summary.addonLeft[0]', '');
      const regex = /\d{1,3}(?=(\d{3})+(?!\d))/g;

      const withPrefix = map(
        totals,
        (total) => `${summaryPrefix} ${replace(toString(total), regex, '$&,')}`
      );

      return {
        component: MultiColTableComponent,
        inputs: {
          data: {
            entries,
            summaryRow: displaySummary
              ? { label: summaryLabel, value: withPrefix }
              : undefined,
          },
          alignValues: align,
        },
      };
    },
  };

  arrayMappers: Record<
    string,
    PBArrayValueMapper2<FormlyFieldConfigWithDisplayViewOptions>
  > = {
    'combined-table': (configItem, model) => {
      if (!isArray(model)) {
        return [];
      }
      const groupById = get(configItem, 'props.groupBy', 'id');
      const groupByLabel = get(configItem, 'props.groupByLabel', '');
      const groupByLabelDefaultValue = get(
        configItem,
        'props.groupByLabelDefaultValue',
        ''
      );
      const cols = get(configItem, ['props', 'columns'], '');
      const align = get(configItem, ['props', 'align'], 'start');

      if (!(isString(groupById) && isString(groupByLabel) && isArray(cols))) {
        return [];
      }

      const grouped = groupBy(model, groupById);

      const components = map(grouped, (group, key) => {
        const title = get(group[0], groupByLabel, groupByLabelDefaultValue);
        const entries = map(group, (item) => {
          const data = map(cols, (col) => {
            const field = find(configItem.fields, { key: col });
            const label = get(field, ['props', 'label'], col);

            const rawValue = get(item, col, '-');
            const value = field
              ? this.simpleTableRowFrom(field, rawValue).value
              : rawValue;

            return { label, value };
          });
          return data;
        });

        return {
          id: configItem.id + '-' + key,
          type: PBValueDataType.FIELD,
          component: ValuesTableComponent,
          inputs: { data: { title, entries }, alignValues: align },
        };
      });
      ('');

      return components;
    },
  };

  getValue(
    data: PBConfigItem<FormlyFieldConfigWithDisplayViewOptions>[],
    model: Record<string, unknown>,
    editorModel: Record<string, string>
  ) {
    const validConfigItems = filter(
      data,
      (configItem) =>
        (configItem.type !== PBConfigItemType.TEMPLATE &&
          has(model, configItem.path)) ||
        configItem.type === PBConfigItemType.TEMPLATE
    );

    return reduce(
      validConfigItems,
      (acc, configItem) => {
        switch (configItem.type) {
          case PBConfigItemType.HEADING: {
            const initialValue =
              get(editorModel, configItem.id) ||
              `<h${configItem.level}>${configItem.value}</h${configItem.level}>`;
            return [
              ...acc,
              {
                id: configItem.id,
                type: PBValueDataType.TEXT,
                value: initialValue,
              } as PBTextValueData,
            ];
          }

          case PBConfigItemType.FIELD: {
            const field = this.createField(configItem, model);

            return field && configItem.field.type !== 'hidden'
              ? ([...acc, field] as PBValueData[])
              : acc;
          }

          case PBConfigItemType.ARRAY: {
            const fieldGroups = this.handleArrayConfig(configItem, model);
            return [...acc, ...fieldGroups];
          }

          case PBConfigItemType.GROUP: {
            const data = this.handleGroupConfig(configItem, model);
            return data ? [...acc, data] : acc;
          }

          default:
            return acc;
        }
      },
      [] as PBValueData[]
    );
  }

  private createField(
    fieldConfig: PBFieldConfig<FormlyFieldConfigWithDisplayViewOptions>,
    model: Record<string, unknown>
  ): PBFieldValueData | undefined {
    const labelValue = this.getFieldValue2(
      fieldConfig.pathString,
      fieldConfig.field,
      model
    );

    if (!labelValue) {
      console.warn('Could not find value accessor for field', fieldConfig);
      return undefined;
    }

    return {
      id: fieldConfig.id,
      type: PBValueDataType.FIELD,
      ...labelValue,
    };
  }

  private handleGroupConfig(
    configItem: PBGroupConfig<FormlyFieldConfigWithDisplayViewOptions>,
    model: object
  ): PBFieldValueData | undefined {
    const groupModel = get(model, configItem.pathString);

    if (!groupModel || !isObject(groupModel)) {
      return;
    }

    const data = compact(
      map(
        reject(configItem.fields, (field) => field.type === 'hidden'),
        (field) => {
          return this.getFieldValue(field.key, field, groupModel);
        }
      )
    );

    const filteredData = reject(
      data,
      (item) => item.rawValue === '' || isNil(item.rawValue)
    );

    if (!filteredData.length) {
      return;
    }

    return {
      id: configItem.id,
      type: PBValueDataType.FIELD,
      component: SimpleTableComponent,
      inputs: { data: filteredData },
    };
  }

  private handleArrayConfig(
    configItem: PBArrayConfig<FormlyFieldConfigWithDisplayViewOptions>,
    model: object
  ): PBFieldValueData[] {
    const groupedValues = get(model, configItem.pathString);
    const typeOverride = configItem.typeOverride;

    if (!groupedValues || !isArray(groupedValues)) {
      return [];
    }

    if (typeOverride) {
      const mapper = get(this.arrayMappers, typeOverride);

      if (!mapper) {
        console.warn('No array mapper found for type', typeOverride);
        return [];
      }

      return mapper(configItem, groupedValues);
    }

    const filteredGroupValues = reject(
      groupedValues,
      (value) => value === undefined || isNil(value)
    );

    const fieldGroups = map(filteredGroupValues, (groupModel, i) => {
      const data = compact(
        map(
          reject(configItem.fields, (field) => field.type === 'hidden'),
          (field) => {
            return this.getFieldValue(field.key as string, field, groupModel);
          }
        )
      );

      return {
        id: `${configItem.id}-${i}`,
        type: PBValueDataType.FIELD,
        component: SimpleTableComponent,
        inputs: { data },
        // ...data,
      };
    });

    return fieldGroups;
  }

  private getFieldValue(
    path: string,
    field: FormlyFieldConfigWithDisplayViewOptions,
    model: object
  ) {
    const rawValue = get(model, path);
    const labelValue = this.simpleTableRowFrom(field, rawValue);
    return labelValue;
  }

  private getFieldValue2(
    path: string,
    field: FormlyFieldConfigWithDisplayViewOptions,
    model: object
  ) {
    const rawValue = get(model, path);
    const labelValue = this.getAccessor(field, rawValue);
    return labelValue;
  }

  private getAccessor(
    field: FormlyFieldConfigWithDisplayViewOptions,
    value: unknown
  ) {
    const accessor = get(this.formlyValueAccessors, field.type);
    if (!accessor) {
      return undefined;
    }

    return accessor(field, value);
  }

  private simpleTableRowFrom(
    field: FormlyFieldConfigWithDisplayViewOptions,
    value: unknown
  ): {
    label: string;
    value: unknown;
    rawValue: unknown;
  } {
    const type = field.displayViewOptions?.typeOverride || field.type;

    const valueAccessor = valueAccessors[type];

    if (!valueAccessor) {
      console.warn('No value accessor found for field', field);
      return {
        label: toString(
          get(field, 'props.longLabel', get(field, 'props.label', ''))
        ),
        value: toString(value),
        rawValue: value,
      };
    }

    return valueAccessor(field, value);
  }
}
